import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './useAuth';
import ScheduleClipModal from './ScheduleClipModal';
import OneTapScheduleModal from './OneTapScheduleModal';
import { fetchOneTapPreferences, hasValidOneTapPreferences } from './oneTapSchedule';
import {
  trackConnectAccountClick,
  trackOneTapScheduleClick,
  trackScheduleAClipClick,
} from './userJourneyTracker';
import {
  buildClipThumbnailUrl,
  fetchClipThumbnailObjectUrl,
  getCalendarClipThumbnailWidth,
} from './clipThumbnailCache';
import './CalendarPage.css';

const CALENDAR_PAGE_BODY_CLASS = 'calendar-page-plain-bg';
const PUBLIC_URL = process.env.PUBLIC_URL || '';

const PLATFORM_ICONS = {
  facebook: `${PUBLIC_URL}/Facebook.png`,
  instagram: `${PUBLIC_URL}/instagram.svg`,
  linkedin: `${PUBLIC_URL}/linkedin.svg`,
  youtube: `${PUBLIC_URL}/YouTube.svg`,
};

const PLATFORMS = [
  { id: 'youtube', label: 'YouTube' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'linkedin', label: 'LinkedIn' },
];

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const VISIBLE_STATUSES = new Set(['pending', 'scheduled', 'publishing', 'published', 'failed']);
const MAX_VISIBLE_POSTS_PER_DAY = 3;
const MONTH_WEEKS = 5;
const MAX_SIDEBAR_UPCOMING_POSTS = 8;
const MOBILE_DAY_COL_WIDE = 180;
const MOBILE_DAY_COL_NARROW = 72;

function canDeleteScheduledPost(post) {
  return post?.status === 'pending' || post?.status === 'failed';
}

function getPostPlatformUrl(post) {
  if (!post) return '';
  const platform = post.platform || 'youtube';
  const payload = post[platform];
  if (!payload || typeof payload !== 'object') return '';
  return payload.permalink || payload.url || '';
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function startOfMonth(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addMonths(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return startOfMonth(d);
}

function formatMonthLabel(monthStart) {
  return monthStart.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function getTimezoneLabel() {
  const offsetHours = -new Date().getTimezoneOffset() / 60;
  const sign = offsetHours >= 0 ? '+' : '';
  return `GMT${sign}${Number.isInteger(offsetHours) ? offsetHours : offsetHours.toFixed(1)}`;
}

function isSameCalendarDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isToday(date) {
  return isSameCalendarDay(date, new Date());
}

function isPastDay(date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day < today;
}

function isScheduledTodayOrFuture(scheduledAtUtc) {
  const scheduled = new Date(scheduledAtUtc);
  if (Number.isNaN(scheduled.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return scheduled >= today;
}

function dayKey(date) {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function buildMonthCells(monthStart) {
  const firstDay = new Date(monthStart);
  const startPad = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
  const gridStart = addDays(monthStart, -startPad);

  return Array.from({ length: MONTH_WEEKS * 7 }, (_, index) => {
    const date = addDays(gridStart, index);
    return {
      date,
      inMonth: date.getMonth() === monthStart.getMonth(),
    };
  });
}

function chunkMonthWeeks(cells) {
  const weeks = [];
  for (let index = 0; index < cells.length; index += 7) {
    weeks.push(cells.slice(index, index + 7));
  }
  return weeks;
}

function buildMobileColumnTemplate(monthCells, postsByDay) {
  return Array.from({ length: 7 }, (_, dayIndex) => {
    const hasPosts = monthCells.some((cell) => {
      const cellDayIndex = cell.date.getDay() === 0 ? 6 : cell.date.getDay() - 1;
      if (cellDayIndex !== dayIndex) return false;
      return (postsByDay.get(dayKey(cell.date)) || []).length > 0;
    });
    const width = hasPosts ? MOBILE_DAY_COL_WIDE : MOBILE_DAY_COL_NARROW;
    return `${width}px`;
  }).join(' ');
}

function getAccountDisplayName(account) {
  if (!account) return '';
  switch (account.platform) {
    case 'youtube':
      return account.channel_title || account.channel_custom_url || 'YouTube';
    case 'instagram': {
      const username = account.ig_username || '';
      if (username) {
        return username.charAt(0).toUpperCase() + username.slice(1);
      }
      return account.page_name || 'Instagram';
    }
    case 'facebook':
      return account.page_name || 'Facebook';
    case 'linkedin':
      return account.name || 'LinkedIn';
    default:
      return account.platform || '';
  }
}

function ThunderIcon({ className = '' }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="cal-one-tap-thunder-grad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#5170ff" />
          <stop offset="100%" stopColor="#ff66c4" />
        </linearGradient>
      </defs>
      <path
        d="M13 2L4 14h6l-1 8 9-13h-6l1-9z"
        fill="url(#cal-one-tap-thunder-grad)"
      />
    </svg>
  );
}

function PlatformIcon({ platform, size = 20, className = '' }) {
  const src = PLATFORM_ICONS[platform];
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      className={className}
      width={size}
      height={size}
      aria-hidden="true"
    />
  );
}

function ClipThumbnail({
  post,
  getFreshTokenRef,
  size = 'md',
  maxWidth = getCalendarClipThumbnailWidth(),
  className = '',
}) {
  const thumbnailUrl = buildClipThumbnailUrl(post?.file_stem, post?.clip_filename, maxWidth);
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (!thumbnailUrl) {
      setSrc(null);
      return undefined;
    }

    let cancelled = false;

    async function load() {
      const objectUrl = await fetchClipThumbnailObjectUrl(
        thumbnailUrl,
        () => getFreshTokenRef.current(),
      );
      if (!cancelled) {
        setSrc(objectUrl);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [thumbnailUrl, getFreshTokenRef]);

  return (
    <div
      className={`cal-clip-thumb cal-clip-thumb--${size} ${className}`.trim()}
      aria-hidden="true"
    >
      {src ? (
        <img src={src} alt="" className="cal-clip-thumb__image" loading="lazy" />
      ) : (
        <div className="cal-clip-thumb__placeholder" />
      )}
      <span className={`cal-clip-thumb__badge cal-clip-thumb__badge--${post.platform || 'youtube'}`}>
        <PlatformIcon platform={post.platform} size={size === 'sm' ? 10 : 12} />
      </span>
    </div>
  );
}

function AccountAvatar({ platform, account, size = 34, className = '' }) {
  const profileImage = account?.profile_image_url;
  const [imgError, setImgError] = useState(false);
  const platformIcon = PLATFORM_ICONS[platform];
  const showProfile = Boolean(profileImage && !imgError);

  if (!account) {
    return <PlatformIcon platform={platform} size={size} className={className} />;
  }

  const initial = getAccountDisplayName(account).replace(/^@/, '').charAt(0).toUpperCase() || '?';

  return (
    <div
      className={`cal-badged-avatar cal-badged-avatar--${platform} ${className}`.trim()}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <div className="cal-badged-avatar__inner">
        {showProfile ? (
          <img
            src={profileImage}
            alt=""
            className="cal-badged-avatar__photo"
            onError={() => setImgError(true)}
          />
        ) : (
          <span className="cal-badged-avatar__initial">{initial}</span>
        )}
      </div>
      {platformIcon && (
        <span className="cal-badged-avatar__badge">
          <img src={platformIcon} alt="" className="cal-badged-avatar__badge-icon" />
        </span>
      )}
    </div>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
      <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function ChevronIcon({ direction = 'left' }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d={direction === 'left' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DayPostChip({ post, getFreshTokenRef }) {
  const timeLabel = new Date(post.scheduled_at_utc).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const title = post.metadata?.title || post.clip_filename || 'Scheduled clip';

  return (
    <div
      className={`cal-day-chip cal-day-chip--${post.status || 'pending'}`}
      title={`${timeLabel} · ${title}`}
    >
      <ClipThumbnail
        post={post}
        getFreshTokenRef={getFreshTokenRef}
        size="sm"
        className="cal-day-chip__thumb"
      />
      <div className="cal-day-chip__text">
        <span className="cal-day-chip__time">{timeLabel}</span>
        <span className="cal-day-chip__title">{title}</span>
      </div>
    </div>
  );
}

function MonthDayCell({
  cell,
  posts,
  isSelected,
  onSelect,
  onScheduleClick,
  onDoubleClick,
  getFreshTokenRef,
}) {
  const { date, inMonth } = cell;
  const past = isPastDay(date);
  const today = isToday(date);
  const visiblePosts = posts.slice(0, MAX_VISIBLE_POSTS_PER_DAY);
  const overflowCount = posts.length - visiblePosts.length;
  const opensScheduleOnClick = inMonth && !past && posts.length === 0;
  const dateLabel = date.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  const handleClick = () => {
    if (opensScheduleOnClick) {
      onScheduleClick(date);
      return;
    }
    onSelect(date);
  };

  return (
    <button
      type="button"
      className={[
        'cal-month-day',
        !inMonth && 'cal-month-day--outside',
        past && 'cal-month-day--past',
        today && 'cal-month-day--today',
        isSelected && 'cal-month-day--selected',
        opensScheduleOnClick && 'cal-month-day--scheduleable',
        posts.length > 0 ? 'cal-month-day--has-posts' : 'cal-month-day--empty',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handleClick}
      onDoubleClick={() => onDoubleClick(date)}
      aria-pressed={isSelected}
      aria-label={
        opensScheduleOnClick
          ? `Schedule a clip on ${dateLabel}`
          : dateLabel
      }
    >
      <span className="cal-month-day__number">{date.getDate()}</span>
      <div className="cal-month-day__posts">
        {visiblePosts.map((post) => (
          <DayPostChip key={post.id} post={post} getFreshTokenRef={getFreshTokenRef} />
        ))}
        {overflowCount > 0 && (
          <span className="cal-month-day__more">+{overflowCount} more</span>
        )}
      </div>
    </button>
  );
}

function ScheduledPostOptionsMenu({
  post,
  onDelete,
  isDeleting,
  menuClassName = '',
  beforeAction,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const canDelete = canDeleteScheduledPost(post);
  const platformUrl = getPostPlatformUrl(post);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onDocPointer = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onDocPointer);
      document.addEventListener('touchstart', onDocPointer, { passive: true });
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('touchstart', onDocPointer);
    };
  }, [menuOpen]);

  const handleDelete = () => {
    if (!beforeAction?.()) return;
    if (!canDelete || isDeleting) return;
    setMenuOpen(false);
    onDelete(post.id);
  };

  const guardAction = (action) => {
    if (beforeAction && !beforeAction()) return;
    action();
  };

  return (
    <div className={`cal-post-card__menu-wrap ${menuClassName}`.trim()} ref={menuRef}>
      <button
        type="button"
        className="cal-icon-btn"
        aria-label="Scheduled post options"
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        onClick={(event) => {
          event.stopPropagation();
          guardAction(() => setMenuOpen((open) => !open));
        }}
      >
        ⋮
      </button>
      {menuOpen && (
        <div className="cal-post-card__menu" role="menu">
          {canDelete ? (
            <button
              type="button"
              className="cal-post-card__menu-item"
              role="menuitem"
              disabled={isDeleting}
              onClick={handleDelete}
            >
              {isDeleting ? 'Deleting…' : 'Delete scheduled post'}
            </button>
          ) : (
            <p className="cal-post-card__menu-hint" role="note">
              {post.status === 'published'
                ? 'Already published'
                : 'Cannot delete this post'}
            </p>
          )}
          {platformUrl && (
            <a
              className="cal-post-card__menu-link"
              role="menuitem"
              href={platformUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => guardAction(() => setMenuOpen(false))}
            >
              Open on {post.platform || 'platform'}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function PostCard({ post, onDelete, isDeleting, getFreshTokenRef, beforeAction }) {
  const title = post.metadata?.title || post.clip_filename || 'Scheduled clip';
  const timeLabel = new Date(post.scheduled_at_utc).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });

  return (
    <article className={`cal-post-card cal-post-card--${post.status || 'pending'}`}>
      <span className="cal-post-card__platform-label">{post.platform}</span>
      <ScheduledPostOptionsMenu
        post={post}
        onDelete={onDelete}
        isDeleting={isDeleting}
        menuClassName="cal-post-card__actions"
        beforeAction={beforeAction}
      />
      <div className="cal-post-card__media">
        <ClipThumbnail
          post={post}
          getFreshTokenRef={getFreshTokenRef}
          size="lg"
          className="cal-post-card__thumb"
        />
      </div>
      <div className="cal-post-card__content">
        <p className="cal-post-card__text" title={title}>
          {title}
        </p>
        <div className="cal-post-card__meta">
          <span className={`cal-status-pill cal-status-pill--${post.status || 'pending'}`}>
            {post.status || 'pending'}
          </span>
          <div className="cal-post-card__time">
            <ClockIcon />
            <span>{timeLabel}</span>
          </div>
        </div>
      </div>
    </article>
  );
}

function UpcomingPostRow({ post, onDelete, isDeleting, getFreshTokenRef, beforeAction }) {
  const title = post.metadata?.title || post.clip_filename || 'Scheduled clip';
  const when = new Date(post.scheduled_at_utc).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  return (
    <div className={`cal-upcoming-row cal-upcoming-row--${post.status || 'pending'}`}>
      <div className="cal-upcoming-row__main">
        <ClipThumbnail
          post={post}
          getFreshTokenRef={getFreshTokenRef}
          size="sm"
          className="cal-upcoming-row__thumb"
        />
        <div className="cal-upcoming-row__info">
          <span className="cal-upcoming-row__title" title={title}>{title}</span>
          <span className="cal-upcoming-row__meta">{when}</span>
        </div>
      </div>
      <ScheduledPostOptionsMenu
        post={post}
        onDelete={onDelete}
        isDeleting={isDeleting}
        beforeAction={beforeAction}
      />
    </div>
  );
}

function PlatformRow({
  platform,
  account,
  isActive,
  connectingPlatform,
  disconnectingPlatform,
  onSelect,
  onConnect,
  onDisconnect,
  beforeAction,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const connected = Boolean(account && account.status === 'active');
  const isConnecting = connectingPlatform === platform.id;
  const isDisconnecting = disconnectingPlatform === platform.id;

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const guardAction = (action) => {
    if (beforeAction && !beforeAction()) return;
    action();
  };

  return (
    <div
      className={`cal-platform-row${isActive ? ' is-active' : ''}${connected ? ' is-connected' : ''}`}
    >
      <button
        type="button"
        className="cal-platform-row__main"
        onClick={() => guardAction(() => onSelect(platform.id))}
        aria-pressed={isActive}
      >
        <AccountAvatar
          platform={platform.id}
          account={connected ? account : null}
          size={34}
          className="cal-platform-row__icon"
        />
        <span className="cal-platform-row__info">
          {connected ? (
            <>
              <span className="cal-platform-row__name">{getAccountDisplayName(account)}</span>
              <span className="cal-platform-row__label">{platform.label}</span>
            </>
          ) : (
            <span className="cal-platform-row__name">{platform.label}</span>
          )}
        </span>
      </button>
      {connected && (
        <div className="cal-platform-row__menu-wrap" ref={menuRef}>
          <button
            type="button"
            className="cal-icon-btn cal-platform-row__menu-btn"
            aria-label={`${platform.label} options`}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            onClick={(event) => {
              event.stopPropagation();
              guardAction(() => setMenuOpen((open) => !open));
            }}
          >
            ⋮
          </button>
          {menuOpen && (
            <div className="cal-platform-row__menu" role="menu">
              <button
                type="button"
                role="menuitem"
                className="cal-platform-row__menu-item"
                disabled={isDisconnecting}
                onClick={() => {
                  guardAction(() => {
                    setMenuOpen(false);
                    onDisconnect(platform.id);
                  });
                }}
              >
                {isDisconnecting ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </div>
          )}
        </div>
      )}
      {!connected && (
        <button
          type="button"
          className="cal-platform-row__connect"
          disabled={isConnecting}
          onClick={() => guardAction(() => onConnect(platform.id))}
        >
          {isConnecting ? '…' : 'Connect'}
        </button>
      )}
    </div>
  );
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, userProfileReady, getFreshToken } = useAuth();
  const getFreshTokenRef = useRef(getFreshToken);
  getFreshTokenRef.current = getFreshToken;

  const profileReady = !isAuthenticated || userProfileReady;

  const runAction = useCallback((action) => {
    if (typeof action === 'function') {
      action();
    }
  }, []);

  const [monthStart, setMonthStart] = useState(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [platformFilter, setPlatformFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [connectingPlatform, setConnectingPlatform] = useState('');
  const [disconnectingPlatform, setDisconnectingPlatform] = useState('');
  const [cancellingId, setCancellingId] = useState('');
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [schedulePrefill, setSchedulePrefill] = useState(null);
  const [oneTapModalOpen, setOneTapModalOpen] = useState(false);
  const [oneTapPreferences, setOneTapPreferences] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMonthSetRef = useRef(false);
  const loadRequestIdRef = useRef(0);
  const dayDetailRef = useRef(null);
  const dayClickTimerRef = useRef(null);
  const pendingDetailScrollRef = useRef(false);

  useEffect(() => {
    document.body.classList.add(CALENDAR_PAGE_BODY_CLASS);
    return () => document.body.classList.remove(CALENDAR_PAGE_BODY_CLASS);
  }, []);

  useEffect(() => {
    if (searchParams.get('setupOneTap') !== '1') return;
    const next = new URLSearchParams(searchParams);
    next.delete('setupOneTap');
    setSearchParams(next, { replace: true });
    setOneTapModalOpen(true);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get('scheduleClip') !== '1') return;
    const fileStem = searchParams.get('fileStem');
    const clipFilename = searchParams.get('clipFilename');
    const next = new URLSearchParams(searchParams);
    next.delete('scheduleClip');
    next.delete('fileStem');
    next.delete('clipFilename');
    next.delete('taskId');
    setSearchParams(next, { replace: true });
    if (!fileStem || !clipFilename) return;
    setSchedulePrefill({ fileStem, clipFilename });
    setScheduleModalOpen(true);
  }, [searchParams, setSearchParams]);

  const oneTapEnabled = hasValidOneTapPreferences(oneTapPreferences);

  useEffect(() => {
    if (!isAuthenticated || !user?.uid) {
      setOneTapPreferences(null);
      return undefined;
    }

    let cancelled = false;

    async function loadOneTapPreferences() {
      try {
        const preferences = await fetchOneTapPreferences(
          () => getFreshTokenRef.current(),
          user.uid,
        );
        if (!cancelled) setOneTapPreferences(preferences);
      } catch {
        if (!cancelled) setOneTapPreferences(null);
      }
    }

    loadOneTapPreferences();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.uid]);

  const loadData = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    setLoading(true);
    setError('');

    try {
      const token = await getFreshTokenRef.current();
      if (!token) throw new Error('Please sign in to view your calendar.');

      const [accountsRes, postsRes] = await Promise.all([
        fetch('/api/connected-accounts', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/schedule', { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      const accountsData = await accountsRes.json().catch(() => ({}));
      const postsData = await postsRes.json().catch(() => ({}));

      if (loadRequestIdRef.current !== requestId) return;

      if (!postsRes.ok) {
        throw new Error(postsData.error || 'Could not load scheduled posts.');
      }

      if (accountsRes.ok) {
        setAccounts(accountsData.accounts || []);
      }

      const visiblePosts = (postsData.posts || []).filter(
        (p) => VISIBLE_STATUSES.has(p.status) && isScheduledTodayOrFuture(p.scheduled_at_utc),
      );
      setPosts(visiblePosts);

      if (!initialMonthSetRef.current && visiblePosts.length > 0) {
        const nearest = visiblePosts.reduce((best, post) => {
          const scheduled = new Date(post.scheduled_at_utc);
          if (Number.isNaN(scheduled.getTime())) return best;
          if (!best || scheduled < best) return scheduled;
          return best;
        }, null);
        if (nearest) {
          setMonthStart(startOfMonth(nearest));
          setSelectedDay(nearest);
          initialMonthSetRef.current = true;
        }
      }
    } catch (err) {
      if (loadRequestIdRef.current === requestId) {
        setError(err.message || 'Something went wrong.');
      }
    } finally {
      if (loadRequestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!authLoading && profileReady) {
      loadData();
    }
  }, [authLoading, profileReady, loadData]);

  useEffect(() => {
    return () => {
      if (dayClickTimerRef.current) {
        clearTimeout(dayClickTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!pendingDetailScrollRef.current || !selectedDay) return;
    pendingDetailScrollRef.current = false;
    dayDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [selectedDay]);

  const monthCells = useMemo(() => buildMonthCells(monthStart), [monthStart]);
  const monthWeeks = useMemo(() => chunkMonthWeeks(monthCells), [monthCells]);

  const filteredPosts = useMemo(() => {
    const monthEnd = addMonths(monthStart, 1);
    return posts.filter((post) => {
      if (platformFilter && post.platform !== platformFilter) return false;
      const scheduled = new Date(post.scheduled_at_utc);
      if (Number.isNaN(scheduled.getTime())) return false;
      return scheduled >= monthStart && scheduled < monthEnd;
    });
  }, [posts, platformFilter, monthStart]);

  const postsByDay = useMemo(() => {
    const map = new Map();
    filteredPosts.forEach((post) => {
      const scheduled = new Date(post.scheduled_at_utc);
      const key = dayKey(scheduled);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(post);
    });
    map.forEach((dayPosts) => {
      dayPosts.sort(
        (a, b) => new Date(a.scheduled_at_utc).getTime() - new Date(b.scheduled_at_utc).getTime()
      );
    });
    return map;
  }, [filteredPosts]);

  const mobileColumnTemplate = useMemo(
    () => buildMobileColumnTemplate(monthCells, postsByDay),
    [monthCells, postsByDay],
  );

  const selectedDayPosts = useMemo(() => {
    if (!selectedDay) return [];
    return postsByDay.get(dayKey(selectedDay)) || [];
  }, [selectedDay, postsByDay]);

  const upcomingPosts = useMemo(() => {
    const today = new Date();
    return posts
      .filter((post) => {
        if (platformFilter && post.platform !== platformFilter) return false;
        if (!canDeleteScheduledPost(post)) return false;
        const scheduled = new Date(post.scheduled_at_utc);
        if (Number.isNaN(scheduled.getTime())) return false;
        return isSameCalendarDay(scheduled, today);
      })
      .sort(
        (a, b) =>
          new Date(a.scheduled_at_utc).getTime() - new Date(b.scheduled_at_utc).getTime()
      )
      .slice(0, MAX_SIDEBAR_UPCOMING_POSTS);
  }, [posts, platformFilter]);

  const handleConnect = async (platformId) => {
    trackConnectAccountClick(platformId);
    setConnectingPlatform(platformId);
    try {
      const token = await getFreshTokenRef.current();
      const origin = encodeURIComponent(window.location.origin);
      const res = await fetch(`/api/oauth/${platformId}/start?origin=${origin}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || `Could not connect ${platformId}`);
      }
      window.location.href = data.authorization_url;
    } catch (err) {
      setError(err.message || 'Connect failed.');
      setConnectingPlatform('');
    }
  };

  const handleDisconnect = async (platformId) => {
    setDisconnectingPlatform(platformId);
    try {
      const token = await getFreshTokenRef.current();
      const res = await fetch(`/api/connected-accounts/${platformId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Could not disconnect ${platformId}`);
      }
      setAccounts((prev) => prev.filter((account) => account.platform !== platformId));
      setPlatformFilter((current) => (current === platformId ? null : current));
    } catch (err) {
      setError(err.message || 'Disconnect failed.');
    } finally {
      setDisconnectingPlatform('');
    }
  };

  const handleCancel = async (postId) => {
    if (!postId) return;
    if (!window.confirm('Delete this scheduled post? It will not be published.')) return;
    setCancellingId(postId);
    try {
      const token = await getFreshTokenRef.current();
      const res = await fetch(`/api/schedule/${postId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Could not delete scheduled post.');
      }
      setPosts((prev) => prev.filter((p) => p.id !== postId));
    } catch (err) {
      setError(err.message || 'Delete failed.');
    } finally {
      setCancellingId('');
    }
  };

  const handlePlatformSelect = (platformId) => {
    setPlatformFilter((current) => (current === platformId ? null : platformId));
  };

  const handleDaySelect = (date) => {
    runAction(() => {
      if (dayClickTimerRef.current) {
        clearTimeout(dayClickTimerRef.current);
      }
      dayClickTimerRef.current = setTimeout(() => {
        dayClickTimerRef.current = null;
        setSelectedDay((current) => (current && isSameCalendarDay(current, date) ? null : date));
      }, 200);
    });
  };

  const handleDayScheduleClick = (date) => {
    runAction(() => {
      if (dayClickTimerRef.current) {
        clearTimeout(dayClickTimerRef.current);
        dayClickTimerRef.current = null;
      }
      trackScheduleAClipClick('calendar_day');
      setSelectedDay(date);
      setScheduleModalOpen(true);
    });
  };

  const handleDayDoubleClick = (date) => {
    runAction(() => {
      if (dayClickTimerRef.current) {
        clearTimeout(dayClickTimerRef.current);
        dayClickTimerRef.current = null;
      }

      const dayPosts = postsByDay.get(dayKey(date)) || [];
      if (dayPosts.length === 0) return;

      if (selectedDay && isSameCalendarDay(selectedDay, date)) {
        dayDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      pendingDetailScrollRef.current = true;
      setSelectedDay(date);
    });
  };

  const handleMonthChange = (delta) => {
    runAction(() => {
      setMonthStart((current) => addMonths(current, delta));
      setSelectedDay(null);
    });
  };

  if (authLoading || (isAuthenticated && !profileReady)) {
    return (
      <div className="calendar-page">
        <div className="calendar-page__centered">Loading…</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="calendar-page">
        <div className="calendar-page__centered">
          <h2>Sign in to view your schedule</h2>
          <p>Connect platforms and schedule clips from your generated shorts.</p>
          <Link to="/" className="cal-btn cal-btn-primary cal-btn-inline">
            Go to dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-page">
      <div className="calendar-shell">
        <aside className="calendar-sidebar">
          <h2 className="calendar-sidebar__title">Connected platforms</h2>

          <div className="calendar-sidebar__actions">
            <div className="cal-schedule-btn-wrap">
              <button
                type="button"
                className="cal-btn cal-btn-schedule-social cal-btn-full"
                onClick={() => runAction(() => {
                  trackScheduleAClipClick('sidebar');
                  setScheduleModalOpen(true);
                })}
              >
                Schedule a clip
              </button>
            </div>
            <div className="cal-schedule-btn-wrap">
              <button
                type="button"
                className="cal-btn cal-btn-one-tap-social cal-btn-full"
                onClick={() => runAction(() => {
                  trackOneTapScheduleClick();
                  setOneTapModalOpen(true);
                })}
              >
                <span className="cal-btn-one-tap-social__main">
                  <ThunderIcon className="cal-btn-one-tap-social__icon" />
                  <span className="cal-btn-one-tap-social__text">1-Tap schedule</span>
                </span>
                <span
                  className={`cal-btn-one-tap-social__status${oneTapEnabled ? ' is-on' : ' is-off'}`}
                  aria-label={oneTapEnabled ? '1-Tap schedule on' : '1-Tap schedule off'}
                >
                  <span className="cal-btn-one-tap-social__dot" aria-hidden="true" />
                  {oneTapEnabled ? 'On' : 'Off'}
                </span>
              </button>
            </div>
          </div>

          <div className="calendar-sidebar__platforms">
            {PLATFORMS.map((platform) => {
              const account = accounts.find((a) => a.platform === platform.id);
              return (
                <PlatformRow
                  key={platform.id}
                  platform={platform}
                  account={account}
                  isActive={platformFilter === platform.id}
                  connectingPlatform={connectingPlatform}
                  disconnectingPlatform={disconnectingPlatform}
                  onSelect={handlePlatformSelect}
                  onConnect={handleConnect}
                  onDisconnect={handleDisconnect}
                  beforeAction={undefined}
                />
              );
            })}
          </div>

          {upcomingPosts.length > 0 && (
            <section className="calendar-sidebar__upcoming">
              <h3 className="calendar-sidebar__upcoming-title">Today&apos;s posts</h3>
              <p className="calendar-sidebar__upcoming-hint">
                Clips scheduled for today. Use ⋮ to delete before they publish.
              </p>
              <div className="calendar-sidebar__upcoming-list">
                {upcomingPosts.map((post) => (
                  <UpcomingPostRow
                    key={post.id}
                    post={post}
                    onDelete={handleCancel}
                    isDeleting={cancellingId === post.id}
                    getFreshTokenRef={getFreshTokenRef}
                    beforeAction={undefined}
                  />
                ))}
              </div>
            </section>
          )}
        </aside>

        <main className="calendar-main">
          <header className="calendar-main__header">
            <div className="calendar-main__heading">
              <h1 className="calendar-main__title">Calendar</h1>
              <span className="calendar-main__timezone">{getTimezoneLabel()}</span>
            </div>

            <div className="cal-month-nav">
              <button
                type="button"
                className="cal-month-nav__btn"
                aria-label="Previous month"
                onClick={() => handleMonthChange(-1)}
              >
                <ChevronIcon direction="left" />
              </button>
              <span className="cal-month-label">{formatMonthLabel(monthStart)}</span>
              <button
                type="button"
                className="cal-month-nav__btn"
                aria-label="Next month"
                onClick={() => handleMonthChange(1)}
              >
                <ChevronIcon direction="right" />
              </button>
              <button
                type="button"
                className="cal-month-nav__today"
                onClick={() => runAction(() => {
                  const today = new Date();
                  setMonthStart(startOfMonth(today));
                  setSelectedDay(today);
                })}
              >
                Today
              </button>
            </div>
          </header>

          {error && (
            <div className="calendar-page__error" role="alert">
              {error}
              <button type="button" onClick={() => runAction(loadData)}>Retry</button>
            </div>
          )}

          <div className="calendar-month-wrap">
            {loading ? (
              <div className="calendar-page__centered calendar-page__centered--in-grid">
                Loading schedule…
              </div>
            ) : (
              <>
                <div className="calendar-month">
                  <div className="calendar-month__weekdays calendar-month__weekdays--desktop">
                    {DAY_LABELS.map((label) => (
                      <div key={label} className="calendar-month__weekday">
                        {label}
                      </div>
                    ))}
                  </div>

                  <div className="calendar-month__grid calendar-month__grid--desktop">
                    {monthCells.map((cell) => (
                      <MonthDayCell
                        key={cell.date.toISOString()}
                        cell={cell}
                        posts={postsByDay.get(dayKey(cell.date)) || []}
                        isSelected={selectedDay ? isSameCalendarDay(selectedDay, cell.date) : false}
                        onSelect={handleDaySelect}
                        onScheduleClick={handleDayScheduleClick}
                        onDoubleClick={handleDayDoubleClick}
                        getFreshTokenRef={getFreshTokenRef}
                      />
                    ))}
                  </div>

                  <div
                    className="calendar-month__mobile-board"
                    style={{ '--cal-mobile-columns': mobileColumnTemplate }}
                  >
                    <div className="calendar-month__weekdays calendar-month__weekdays--mobile">
                      {DAY_LABELS.map((label) => (
                        <div key={label} className="calendar-month__weekday">
                          {label}
                        </div>
                      ))}
                    </div>

                    <div className="calendar-month__weeks calendar-month__weeks--mobile">
                      {monthWeeks.map((week, weekIndex) => (
                        <div
                          key={`week-${weekIndex}`}
                          className="calendar-month__week-row"
                        >
                          {week.map((cell) => (
                            <MonthDayCell
                              key={cell.date.toISOString()}
                              cell={cell}
                              posts={postsByDay.get(dayKey(cell.date)) || []}
                              isSelected={selectedDay ? isSameCalendarDay(selectedDay, cell.date) : false}
                              onSelect={handleDaySelect}
                              onScheduleClick={handleDayScheduleClick}
                              onDoubleClick={handleDayDoubleClick}
                              getFreshTokenRef={getFreshTokenRef}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>

                  {!loading && filteredPosts.length === 0 && (
                    <div className="calendar-month__empty">
                      <p>No scheduled clips this month.</p>
                      <button
                        type="button"
                        className="cal-btn cal-btn-outline cal-btn-inline"
                        onClick={() => runAction(() => {
                          trackScheduleAClipClick('month_empty');
                          setScheduleModalOpen(true);
                        })}
                      >
                        Schedule a clip
                      </button>
                    </div>
                  )}
                </div>

                {selectedDay && (
                  <section ref={dayDetailRef} className="cal-day-detail">
                    <h2 className="cal-day-detail__title">
                      {selectedDay.toLocaleDateString(undefined, {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </h2>
                    {selectedDayPosts.length === 0 ? (
                      <p className="cal-day-detail__empty">No scheduled clips on this day.</p>
                    ) : (
                      <div className="cal-day-detail__posts">
                        {selectedDayPosts.map((post) => (
                          <PostCard
                            key={post.id}
                            post={post}
                            onDelete={handleCancel}
                            isDeleting={cancellingId === post.id}
                            getFreshTokenRef={getFreshTokenRef}
                            beforeAction={undefined}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {isAuthenticated && (
        <>
      <ScheduleClipModal
        isOpen={scheduleModalOpen}
        onClose={() => {
          setScheduleModalOpen(false);
          setSchedulePrefill(null);
        }}
        accounts={accounts}
        getFreshTokenRef={getFreshTokenRef}
        initialScheduleDate={selectedDay}
        initialFileStem={schedulePrefill?.fileStem || ''}
        initialClipFilename={schedulePrefill?.clipFilename || ''}
        onScheduled={loadData}
      />

      <OneTapScheduleModal
        isOpen={oneTapModalOpen}
        onClose={() => setOneTapModalOpen(false)}
        accounts={accounts}
        userId={user?.uid || ''}
        getFreshTokenRef={getFreshTokenRef}
        onSaved={(saved) => setOneTapPreferences(saved)}
      />
        </>
      )}
    </div>
  );
}
